import React, { useContext, useState } from "react";

import { Autorenew } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";

import { toUUID } from "../../helpers/s12-helpers";
import mainStyles from "../../styles";
import { updateUserAdminFlag } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_REGION_CODE } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { RegionCode } from "types/region";

enum StatusValue {
  ENABLE = "enable",
  DISABLE = "disable",
}
type Status = StatusValue.ENABLE | StatusValue.DISABLE;

interface StatusIcon {
  displaySuccess: boolean;
  opacity: number;
}

const SuperAdminFlag = () => {
  const classes = mainStyles();
  const [regionCode, setRegionCode] = useState<RegionCode>(DEFAULT_REGION_CODE);
  const [adminFlag, setAdminFlag] = useState<Status>(StatusValue.DISABLE);
  const [userId, setUserId] = useState("");
  const [statusIcon, setStatusIcon] = useState<StatusIcon>({
    displaySuccess: false,
    opacity: 0,
  });
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const { showError } = useContext(SnackbarContext);

  const handleUpdateUserAdminFlag = () => {
    setShowLoadingSpinner(true);
    updateUserAdminFlag(
      {
        userId: toUUID(userId),
        adminFlag: adminFlag === StatusValue.ENABLE,
      },
      callOptionsHeaders(regionCode, userId),
    )
      .then(() => {
        setStatusIcon({ displaySuccess: true, opacity: 100 });
      })
      .catch((e) => {
        setStatusIcon({ displaySuccess: false, opacity: 100 });
        showError(e);
      })
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const handleRegionChange = (event: SelectChangeEvent<string>) => {
    setRegionCode(event.target.value as RegionCode);
  };

  const handleFlagChange = (event: SelectChangeEvent<string>) => {
    setAdminFlag(event.target.value as Status);
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sx={classes.spaceSearch}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
        >
          <TextField
            required
            id="input-admin-flag-userid"
            label="User ID"
            value={userId}
            onChange={(event) => {
              setUserId(event.target.value);
            }}
            variant="outlined"
            size="small"
            inputProps={{
              "data-testid": "input-admin-flag-userid",
            }}
            style={{ width: 400 }}
          />
          <Select
            labelId="select-admin-flag-label"
            id="select-admin-flag-region"
            onChange={handleRegionChange}
            value={regionCode}
            size="small"
            inputProps={{
              "data-testid": "select-admin-flag-region",
            }}
          >
            <MenuItem value={"us"} key={"region-us"}>
              US
            </MenuItem>
            <MenuItem value={"au"} key={"region-au"}>
              AU
            </MenuItem>
            <MenuItem value={"eu"} key={"region-eu"}>
              EU
            </MenuItem>
          </Select>
          <Select
            labelId="select-admin-flag-status"
            id="select-admin-flag-status"
            onChange={handleFlagChange}
            value={adminFlag}
            size="small"
            inputProps={{
              "data-testid": "select-admin-flag-status",
            }}
          >
            <MenuItem
              value={"enable"}
              key={"admin-flag-enable"}
              data-testid={"admin-flag-enable"}
            >
              ENABLE
            </MenuItem>
            <MenuItem
              value={"disable"}
              key={"admin-flag-disable"}
              data-testid={"admin-flag-disable"}
            >
              DISABLE
            </MenuItem>
          </Select>
          <Button
            disabled={showLoadingSpinner || userId === ""}
            variant="contained"
            color="primary"
            onClick={() => {
              handleUpdateUserAdminFlag();
              setStatusIcon({ displaySuccess: true, opacity: 0 });
            }}
            data-testid="btn-admin-flag-update"
            startIcon={
              showLoadingSpinner ? <Autorenew className="spin-1s" /> : null
            }
          >
            Update Flag
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setUserId("");
              setStatusIcon({ displaySuccess: false, opacity: 0 });
            }}
            data-testid="btn-admin-flag-clear"
          >
            Clear
          </Button>
          {statusIcon.displaySuccess ? (
            <CheckCircleIcon
              fontSize="medium"
              color="success"
              style={{ opacity: statusIcon.opacity }}
              data-testid={"icon-admin-flag-success"}
            />
          ) : (
            <CancelIcon
              fontSize="medium"
              color="error"
              style={{ opacity: statusIcon.opacity }}
              data-testid={"icon-admin-flag-failed"}
            ></CancelIcon>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default SuperAdminFlag;
